import React, { useState, useEffect } from 'react';
import './NodeDetail.css';

// Extract display name and type from node ID
const parseNodeInfo = (nodeId, nodeType) => {
  let id = String(nodeId || '');
  // Strip schema-driven multilayer prefix (e.g. "row::", "cluster::").
  const sep = id.indexOf('::');
  if (sep !== -1) id = id.slice(sep + 2);
  const typeSuffixes = {
    '_Affiliation': 'Affiliation',
    '_Organization': 'Organization',
    '_Beamline': 'Beamline'
  };
  let displayName = id;
  let inferredType = nodeType || 'Organization';
  for (const [suffix, type] of Object.entries(typeSuffixes)) {
    if (id.endsWith(suffix)) {
      displayName = id.substring(0, id.length - suffix.length);
      inferredType = type;
      break;
    }
  }
  displayName = displayName.replace(/['"]/g, '').replace(/_/g, ' ');
  return { displayName, inferredType };
};

/**
 * Single node detail card – used inside NodeDetail for each selected node.
 */
// Map MARVIN layer types to a CSS class suffix for the connection dot colour.
const LAYER_TYPE_CLASS = {
  'Cluster':      'cluster',
  'Affiliation':  'affiliation',
  'Author':       'author',
  'WoS Category': 'wos-category',
  'Paper':        'paper',
};

function SingleNodeCard({ node, minLinkWeight, isMulti, onDeselect }) {
  const { displayName, inferredType } = parseNodeInfo(node.id, node.type);
  // Detect schema-driven multilayer node (id contains '::')
  const isMultilayer = typeof node.id === 'string' && node.id.includes('::');

  const allConnections = Array.isArray(node.connections) ? node.connections : [];
  const connections = allConnections.filter(conn => {
    if (typeof conn === 'object' && conn.weight != null) return conn.weight >= minLinkWeight;
    return true;
  });
  const hiddenConnectionCount = allConnections.length - connections.length;
  const proposals = Array.isArray(node.proposals) ? node.proposals : [];
  const papers    = Array.isArray(node.papers)    ? node.papers    : [];

  const paperCount   = isMultilayer ? papers.length   : proposals.length;
  const recordLabel  = isMultilayer ? (node.recordLabel || 'Paper') : 'Proposal';
  const weightLabel  = `shared ${recordLabel.toLowerCase()}s`;
  const connectionScope = isMultilayer ? node.connectionScope : null;

  return (
    <div className={`single-node-card${isMulti ? ' multi' : ''}`}>
      <div className="single-node-header">
        <span className="single-node-title" title={displayName}>
          <strong>{inferredType}:</strong> {displayName}
        </span>
        {isMulti && onDeselect && (
          <button className="deselect-node-btn" onClick={() => onDeselect(node.id)} title="Remove from selection">×</button>
        )}
      </div>

      {!isMultilayer && (
        <div className="node-info">
          {node.description && <p><strong>Description:</strong> {node.description}</p>}
          {node.organizations && (
            <p><strong>Organizations:</strong> {Array.isArray(node.organizations) ? node.organizations.join(', ') : node.organizations}</p>
          )}
        </div>
      )}

      <div className="detail-sections-row">
        {/* Connections */}
        <div className="detail-section">
          <h4>Connections ({connections.length})</h4>
          {connectionScope && (
            <p className="connections-filter-note" title="Only layers shown in the active view are listed">
              within “{connectionScope}”
            </p>
          )}
          {hiddenConnectionCount > 0 && (
            <p className="connections-filter-note">
              {hiddenConnectionCount} weaker connection{hiddenConnectionCount !== 1 ? 's' : ''} hidden
            </p>
          )}
          {connections.length > 0 ? (
            <ul className="connections-list">
              {connections.map((connection, index) => {
                const rawStr    = typeof connection === 'object' ? (connection.name || connection.id) : connection;
                const connWeight = typeof connection === 'object' ? connection.weight : null;
                const connType  = typeof connection === 'object' ? connection.type : null;
                const connColor = typeof connection === 'object' ? connection.color : null;
                let connLabel = rawStr;
                let connClass = 'conn-organization';

                if (isMultilayer && connType) {
                  // Schema-driven: name is already clean, use type for class
                  connClass = `conn-${LAYER_TYPE_CLASS[connType] || 'organization'}`;
                } else {
                  // MaxIV suffix-based detection
                  if (rawStr.endsWith('_Beamline')) {
                    connLabel = rawStr.replace('_Beamline', '');
                    connClass = 'conn-beamline';
                  } else if (rawStr.endsWith('_Affiliation')) {
                    connLabel = rawStr.replace('_Affiliation', '');
                    connClass = 'conn-affiliation';
                  }
                }
                return (
                  <li key={index} className={connClass}>
                    <span className="conn-symbol" style={connColor ? { color: connColor } : undefined}>●</span>
                    {isMultilayer && connType && (
                      <span className="conn-type-badge">{connType}</span>
                    )}
                    <span className="conn-label">{connLabel}</span>
                    {connWeight != null && (
                      <span className="conn-weight" title={`${connWeight} ${weightLabel}`}>{connWeight}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : <p>No connections available.</p>}
        </div>

        {/* Papers (multilayer) or Proposals (MaxIV/A2A) */}
        <div className="detail-section">
          <h4>{recordLabel}s ({paperCount})</h4>
          {isMultilayer ? (
            papers.length > 0 ? (
              <ul className="proposals-list">
                {papers.map((paper, index) => {
                  // Generic schema path; WoS exports keep the richer legacy rendering below.
                  if (paper._title !== undefined && !paper['Article Title']) {
                    return (
                      <li key={index}>
                        <div className="proposal-header">{paper._title || 'Untitled'}</div>
                        {Array.isArray(paper._meta) && paper._meta.length > 0 && (
                          <div className="proposal-meta">
                            {paper._meta.map(([k, v]) => (
                              <span key={k} className="proposal-cycle" title={k}>{String(v).replace(/_/g, ' ')}</span>
                            ))}
                          </div>
                        )}
                      </li>
                    );
                  }
                  const title   = paper['Article Title'] || paper['Title'] || '';
                  const wosId   = paper['UT (Unique WOS ID)'] || '';
                  const year    = paper['Publication Year'] || '';
                  const journal = paper['Source Title'] || '';
                  const rawAuthors = paper['Authors'] || '';
                  const authorList = String(rawAuthors).split(';').map(a => a.trim()).filter(Boolean);
                  const authorStr  = authorList.length > 3
                    ? authorList.slice(0, 3).join('; ') + ` +${authorList.length - 3}`
                    : authorList.join('; ');
                  const clusters = paper['EXC_clusters'] || paper['cluster_name'] || '';
                  return (
                    <li key={index}>
                      <div className="proposal-header">
                        {title || wosId || 'Untitled'}
                      </div>
                      <div className="proposal-meta">
                        {year    && <span className="proposal-cycle">{year}</span>}
                        {journal && <span className="proposal-industry">{journal}</span>}
                        {authorStr && <span className="proposal-topic">{authorStr}</span>}
                        {clusters && clusters !== '' && (
                          <span className="proposal-cycle" title="Excellence Clusters">
                            {String(clusters).replace(/[\[\]'"]/g, '').trim()}
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : <p>No {recordLabel.toLowerCase()}s available.</p>
          ) : (
            proposals.length > 0 ? (
              <ul className="proposals-list">
                {proposals.map((proposal, index) => (
                  <li key={index}>
                    {typeof proposal === 'object' ? (
                      <>
                        <div className="proposal-header">
                          <strong>{proposal['Proposal ID'] || 'N/A'}</strong>: {proposal.Title || 'No Title'}
                        </div>
                        {(proposal.predicted_industry || proposal.topic_name || proposal.Cycle_split || proposal.Cycle) && (
                          <div className="proposal-meta">
                            {(proposal.Cycle_split || proposal.Cycle) && (
                              <span className="proposal-cycle">{proposal.Cycle_split || proposal.Cycle}</span>
                            )}
                            {proposal.predicted_industry && <span className="proposal-industry">Industry: {proposal.predicted_industry}</span>}
                            {proposal.topic_name && <span className="proposal-topic">Topic: {proposal.topic_name}</span>}
                          </div>
                        )}
                      </>
                    ) : proposal}
                  </li>
                ))}
              </ul>
            ) : <p>No proposals available.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function NodeDetail({ nodes = [], selectedNodeIds, onClose, onDeselectNode, theme, themeColors, minLinkWeight = 1 }) {
  const [minimized, setMinimized] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // Tab index for multi-node view

  // Reset to expanded when selection changes
  useEffect(() => {
    setMinimized(false);
  }, [selectedNodeIds]);

  // Clamp active tab
  useEffect(() => {
    if (activeTab >= nodes.length) setActiveTab(Math.max(0, nodes.length - 1));
  }, [nodes.length, activeTab]);

  if (!nodes || nodes.length === 0) {
    return <div className="placeholder">Select a node in the network to see details.</div>;
  }

  const isMulti = nodes.length > 1;
  // Detect multilayer (any node id contains '::')
  const isMultilayer = nodes.some(n => typeof n.id === 'string' && n.id.includes('::'));
  const recordLabel = (isMultilayer ? (nodes.find(n => n.recordLabel)?.recordLabel || 'paper') : 'proposal').toLowerCase();
  // Deduplicate records across nodes
  const allRecordIds = new Set();
  nodes.forEach(n => {
    const list = isMultilayer
      ? (Array.isArray(n.papers) ? n.papers : [])
      : (Array.isArray(n.proposals) ? n.proposals : []);
    list.forEach(p => {
      const pid = typeof p === 'object'
        ? (p['UT (Unique WOS ID)'] || p['Proposal ID'] || p._title || JSON.stringify(p))
        : p;
      allRecordIds.add(pid);
    });
  });
  const totalProposals = allRecordIds.size;
  // Deduplicate connections across nodes
  const allConnectionIds = new Set();
  nodes.forEach(n => {
    if (Array.isArray(n.connections)) {
      n.connections.forEach(c => {
        const cid = (typeof c === 'object' && c.id) ? c.id : c;
        allConnectionIds.add(cid);
      });
    }
  });
  const totalConnections = allConnectionIds.size;

  if (minimized) {
    return (
      <div className="node-detail-panel node-detail-minimized">
        <div className="minimized-bar">
          <span className="minimized-label">
            <strong>{nodes.length} node{nodes.length > 1 ? 's' : ''} selected</strong>
            {isMulti && <span className="minimized-summary"> — {totalProposals} {recordLabel}s, {totalConnections} connections</span>}
            {!isMulti && (() => {
              const { displayName, inferredType } = parseNodeInfo(nodes[0].id, nodes[0].type);
              return <> — <strong>{inferredType}:</strong> {displayName}</>;
            })()}
          </span>
          <div className="minimized-actions">
            <button className="minimize-button" onClick={() => setMinimized(false)} aria-label="Expand" title="Expand details">▲</button>
            <button className="close-button-inline" onClick={onClose} aria-label="Close" title="Close">×</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="node-detail-panel">
      <div className="node-detail-header-row">
        <h3>
          {isMulti ? `${nodes.length} Nodes Selected` : `Node Details: ${parseNodeInfo(nodes[0].id, nodes[0].type).displayName}`}
        </h3>
        <div className="header-actions">
          <button className="minimize-button" onClick={() => setMinimized(true)} aria-label="Minimize" title="Minimize panel">▼</button>
          <button className="close-button" onClick={onClose} aria-label="Close" title="Close all">×</button>
        </div>
      </div>

      {isMulti && (
        <div className="multi-summary-row">
          <span className="multi-stat">{totalProposals} {recordLabel}s</span>
          <span className="multi-stat-sep">•</span>
          <span className="multi-stat">{totalConnections} connections</span>
          <span className="multi-hint-inline">Ctrl+click to add/remove</span>
        </div>
      )}

      {/* Tab bar for multi-select */}
      {isMulti && (
        <div className="node-tabs">
          {nodes.map((n, i) => {
            const { displayName } = parseNodeInfo(n.id, n.type);
            return (
              <button
                key={n.id}
                className={`node-tab${activeTab === i ? ' active' : ''}`}
                onClick={() => setActiveTab(i)}
                title={displayName}
              >
                {displayName.length > 14 ? displayName.slice(0, 12) + '…' : displayName}
              </button>
            );
          })}
        </div>
      )}

      {/* Show active tab's node card */}
      <SingleNodeCard
        node={nodes[activeTab] || nodes[0]}
        minLinkWeight={minLinkWeight}
        isMulti={isMulti}
        onDeselect={onDeselectNode}
      />
    </div>
  );
}

export default NodeDetail;